// Precat.io Main App Component
// Phase 11: Added Privacy Policy route
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './components/AuthContext';
import Home from './components/Home';
import Chat from './components/Chat';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import SubscriptionModal from './components/SubscriptionModal';
import AdminDashboard from './components/AdminDashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import './App.css';

function AppContent() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [showAuthModalOnReturn, setShowAuthModalOnReturn] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  
  const { isAuthenticated, isAdmin, loading } = useAuth();

  // Check URL and set view - runs on every render if path changes
  useEffect(() => {
    const checkPath = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash;

      if (hash === '#admin' || path === '/admin') {
        setCurrentView('admin');
      } else if (path === '/privacy' || path === '/privacy-policy') {
        setCurrentView('privacy');
      } else if (path === '/reset-password' || (search.includes('token=') && path.includes('reset'))) {
        setCurrentView('reset-password');
      } else if (path === '/verify-email' || (search.includes('token=') && path.includes('verify'))) {
        setCurrentView('verify-email');
      } else if (currentView === 'admin' || currentView === 'reset-password' || currentView === 'verify-email' || currentView === 'privacy') {
        // If we're leaving a special view, go back to home
        setCurrentView('home');
      }
    };

    checkPath();

    // Listen for back/forward browser navigation
    window.addEventListener('popstate', checkPath);
    return () => window.removeEventListener('popstate', checkPath);
  }, [currentView]);

  const handleSelectConversation = (conversationType) => {
    setSelectedConversation(conversationType);
    setResumeSessionId(null);
    setCurrentView('chat');
  };

  const handleResumeConversation = ({ sessionId, conversationType }) => {
    setSelectedConversation(conversationType);
    setResumeSessionId(sessionId);
    setCurrentView('chat');
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedConversation(null);
    setResumeSessionId(null);
    window.history.pushState({}, '', '/');
  };

  const handleResetComplete = (openModal) => {
    setCurrentView('home');
    if (openModal === 'login') {
      setShowAuthModalOnReturn('login');
    }
  };

  const handleVerifyComplete = () => {
    setCurrentView('home');
  };

  const handleShowSubscription = () => {
    setShowSubscriptionModal(true);
  };

  // Wait for auth to load before showing admin
  if (loading && currentView === 'admin') {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh', 
        backgroundColor: '#1a1a1a',
        color: '#d4af37',
        fontSize: '1.25rem'
      }}>
        Loading...
      </div>
    );
  }

  // Render based on current view
  if (currentView === 'reset-password') {
    return <ResetPassword onComplete={handleResetComplete} />;
  }

  if (currentView === 'verify-email') {
    return <VerifyEmail onComplete={handleVerifyComplete} />;
  }

  if (currentView === 'privacy') {
    return <PrivacyPolicy onBack={handleBackToHome} />;
  }

  if (currentView === 'admin') {
    // Admin dashboard - requires admin access
    if (!isAuthenticated) {
      // Redirect to home if not logged in
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh', 
          backgroundColor: '#1a1a1a',
          color: '#e0e0e0'
        }}>
          <p>Please log in to access admin panel...</p>
        </div>
      );
    }
    
    if (!isAdmin) {
      // Show forbidden message if not admin
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh', 
          backgroundColor: '#1a1a1a',
          color: '#e0e0e0',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <h1 style={{ color: '#d4af37' }}>Access Denied</h1>
          <p>You do not have admin privileges.</p>
          <button 
            onClick={handleBackToHome} 
            style={{
              background: '#d4af37',
              color: '#1a1a1a',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Return Home
          </button>
        </div>
      );
    }
    
    return <AdminDashboard onBack={handleBackToHome} />;
  }

  if (currentView === 'chat') {
    return (
      <>
        <Chat 
          conversationType={selectedConversation} 
          resumeSessionId={resumeSessionId}
          onBack={handleBackToHome}
          onShowSubscription={handleShowSubscription}
        />
        <SubscriptionModal 
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
        />
      </>
    );
  }

  // Default: home
  return (
    <Home 
      onSelectConversation={handleSelectConversation}
      onResumeConversation={handleResumeConversation}
      initialAuthModal={showAuthModalOnReturn}
      onAuthModalShown={() => setShowAuthModalOnReturn(null)}
    />
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <AppContent />
      </div>
    </AuthProvider>
  );
}

export default App;
